<template>
  <div class="flex flex-col lg:flex-row w-full h-screen">
    <!-- Left side -->
    <div
      class="w-full lg:w-1/2 h-[40vh] lg:h-full flex items-center justify-center faqs"
      style="
        background: linear-gradient(to bottom right, #0b1734 0%, #1a3a8f 135%);
      "
    >
      FAQS
    </div>

    <!-- Mobile only -->
    <div class="block md:hidden">
      <Faqs :faqTabs="faqTabs" layout="mobile" />
    </div>

    <!-- Tablet only -->
    <div class="hidden md:block lg:hidden">
      <Faqs :faqTabs="faqTabs" layout="tablet" />
    </div>

    <!-- Desktop only -->
    <div class="hidden lg:block w-1/2">
      <Faqs :faqTabs="faqTabs" layout="desktop" />
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";
// import { PhMagnifyingGlass, PhX } from "@phosphor-icons/vue"; // assuming you use phosphor icons
import Faqs from "@/components/FaqsCom.vue";

const faqTabs = ref([
  {
    title: "Apply",
    questions: [
      {
        question: "How do I apply to see if I qualify?",
        answer:
          "You can apply for FutureRent through our online web approval form. Pre-approval results are available in just 1 minute, with final approval subject to an affordability review.",
      },
      {
        question: "What documents are required for my application?",
        answer: {
          intro:
            "Once your application is submitted and pre-approved, we'll need the following <strong>four documents</strong> to determine your eligibility:",
          list: [
            "<span class='font-bold'>Latest 3 months of personal bank statements</span>: Ensure they show your income.",
            "<span class='font-bold'>Latest month’s payslip</span>.",
            "<span class='font-bold'>Valid driver’s license</span>: Must be for the applicant.",
            "<span class='font-bold'>Colour copy of your ID</span>: Ensure it's clear and facing up.",
            "<span class='font-bold'>Proof of address</span>: other than your bank statement, e.g. Wifi invoice, utility bill.",
          ],
          closing:
            "You can email these to <a href='mailto:caitlin@futurerent.co.za' class='text-blue-600 underline'>caitlin@futurerent.co.za</a> <br /> Got questions? <a href='http://api.whatsapp.com/send?phone=27872650918&amp;text=Start' class='text-blue-600 underline'>Whatsapp Us</a>",
        },
      },
      {
        question: "What is the deposit for the rental?",
        answer:
          "All our vehicles require a minimum deposit. Deposits start from as low as R10,000.00 with most being around 10% of the vehicle price.",
      },
      {
        question: "How long are the rental agreements?",
        answer:
          "The rental agreement is flexible, but you can buy out the remaining term at any time or purchase the vehicle for the current market value of the vehicle, after 36 months.",
      },
      {
        question: "Can I use the vehicle for Uber or Bolt?",
        answer:
          "No, FutureRent vehicles cannot be used for ride-hailing, as our insurance policy does not cover this purpose.",
      },
      {
        question: "I have bad credit/under debt review, can I still apply?",
        answer:
          "Yes, at FutureRent, you can still apply even with bad credit or under debt review. Our rent-to-buy program is inclusive and flexible, accommodating individuals in diverse financial situations.",
      },
      {
        question:
          "Am I able to upgrade or swap the vehicle after a period of time?",
        answer:
          "You can upgrade your subscription after 6 months, subject to our return conditions. However, we cannot guarantee approval for your second vehicle application. Upgrades will also require a new deposit.",
      },
      {
        question: "Am I able to return the car?",
        answer:
          "Yes, you can return the car anytime after the minimum period of 6 months. To do so, you would need to pay a cancellation fee equal to one monthly rental payment, provided your account is up to date.",
      },
      {
        question: "Am I able to purchase the vehicle I am currently renting?",
        answer:
          "Yes, you have the option to purchase the vehicle from us at any time during the agreement. Alternatively, at the end of the 36-month agreement, you can buy the vehicle for the current market value.",
      },
      {
        question: "Does the vehicle have insurance?",
        answer:
          "All our vehicles at FutureRent come with full comprehensive insurance coverage. This insurance includes protection for accidents, theft, and fire, providing peace of mind to our customers throughout the rental period. For more details on the terms and conditions of our insurance coverage, please refer to your rental agreement or contact our customer service team.",
      },
    ],
  },
  {
    title: "Vehicles",
    questions: [
      {
        question:
          "Am I able to trade my current vehicle in for a higher-grade model?",
        answer:
          "Yes, you are able to do so after 6 months, however a new initiation fee is payable.",
      },
      {
        question:
          "Am I able to use my current car as a trade-in deposit toward the FutureRent rental vehicle?",
        answer:
          "Unfortunately, we don't offer trade-ins as a deposit for a FutureRent vehicle.",
      },
      {
        question:
          "Am I allowed to rent a vehicle through FutureRent I find at a private seller?",
        answer:
          "FutureRent sources its vehicles exclusively from accredited suppliers and manufacturers to ensure the highest standards of quality and reliability. We do not purchase vehicles from private sellers for rentals.",
      },
      {
        question: "What types of vehicles are available through FutureRent?",
        answer: {
          intro:
            "FutureRent offers a selection of vehicles available in our showroom, all of our vehicles have passed our rigorous inspections and typically still fall under the OEM warranty.",
          closing:
            "Certain brands are restricted—contact us at <a href='mailto:caitlin@futurerent.co.za' class='text-blue-600 underline'>caitlin@futurerent.co.za</a> for more details.",
        },
      },
    ],
  },
  {
    title: "Insurance and Maintenance",
    questions: [
      {
        question: "Do I get unlimited mileage?",
        answer:
          "Mileage is limited to 1500 kms per month to keep the vehicle in line with both their maintenance and service plans. Additional mileage is charged at R1.50 per km or can be bought at bundled pricing of R1 per km.",
      },
      {
        question: "Do I get a courtesy car if my car is in for repairs?",
        answer:
          "No, FutureRent does not offer courtesy cars for customers whose vehicles are in for repairs. We recommend arranging alternative transportation during this time to ensure minimal disruption to your daily activities. For any further assistance or inquiries regarding repair services, please reach out to our customer service team.",
      },
      {
        question:
          "Who is responsible for maintenance, servicing and licensing?",
        answer:
          "Minor servicing, maintenance (with exceptions and limitations) as well as licensing is included in your rental agreement with FutureRent. For specific details on coverage and exclusions, please refer to your rental agreement or contact our customer service team for assistance.",
      },
    ],
  },
]);
</script>

<style scoped>
/* Fade slide transition */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateX(10px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateX(-10px);
}

/* Main FAQ title */
.faqs {
  font-size: 9rem;
  color: #fff;
}

/* === Extra responsiveness for 2010–2025 devices === */

/* Tiny old phones (<360px wide) */
@media (max-width: 360px) {
  .faqs {
    font-size: 3rem;
  }
  button {
    padding: 0.25rem 0.5rem;
    font-size: 0.7rem;
  }
}

/* Small phones (<480px) */
@media (max-width: 480px) {
  .faqs {
    font-size: 4rem;
  }
}

/* Tablets portrait (768px and below) */
@media (max-width: 767px) {
  .faqs {
    font-size: 5rem;
    height: 25% !important;
  }
}

/* Extra large TVs (>2560px wide) */
@media (min-width: 2560px) {
  .faqs {
    font-size: 12rem;
  }
  .lap {
    padding-right: 18rem !important;
    padding-left: 0rem !important;
  }
  body {
    zoom: 1.2;
  }
}
</style>
